import React, { useEffect, useState } from 'react';
import fp from 'lodash/fp';
import bytes from 'bytes';
import { rawRequest, contentTypeJson } from '@factorin/rest';

import { Label, Link, Text, Button, Note, LoadingIndicator, ClassName, cn, animation, TransitionComponent, Icon } from 'Modules/ui2';
import download from 'Modules/ui2/icons/download.sprite.svg';
import { wrapRequest } from 'Modules/core/api';
import { useSelector } from 'Modules/core/redux';

import documentsArrow from '../Icons/documentsArrow.sprite.svg';
import styles from './index.module.scss';


export interface Document {
    Uid: string;
    DocumentUid: string;
    Date: Date;
    Number: string;
    DocumentTypeUid: string;
    DocumentTypeName: string;
    DocumentTypeCode: string;
    FileUid: string;
    FileName: string;
    FileExtension: string;
    FileSize: number;
    IsPublic: boolean;
    IsDeleted: boolean;
    IsSigned: boolean;
}

const getPublicLink = ({ Uid }: Document, clientofficeUrl: string) => fp.pipe(
    fp.filter(fp.identity),
    fp.join(''),
)([
    clientofficeUrl,
    '/api/v1',
    `/companydocuments/public/${Uid}/download`,
]);

type DocumentsProps = ClassName & {
    isDefaultOpen?: boolean;
}

export const Documents = React.memo<DocumentsProps>(({ className, isDefaultOpen = false }) => {
    const [docs, setDocs] = useState<Document[]>(null);
    const clientofficeUrl = useSelector('landing.config.clientofficeUrl');
    useEffect(
        () => {
            if (clientofficeUrl) {
                wrapRequest(rawRequest, {
                    method: 'GET',
                    url: `${clientofficeUrl}/api/v1/companydocuments/public`,
                    headers: {
                        Accept: contentTypeJson,
                        'Accept-Language': 'ru',
                        'Content-Type': contentTypeJson,
                    },
                }).then((docs: Document[]) => fp.isArray(docs) && setDocs(docs));
            }
        },
        [clientofficeUrl],
    );

    const [isOpen, setOpen] = useState(isDefaultOpen);

    return (
        <div className={cn(styles.container, className)}>
            <Label
                component={Button}
                variant='auxiliary'
                size='32'
                textSize='21-24'
                weight='demibold'
                className={isOpen && styles.isOpen}
                onClick={() => setOpen(open => !open)}
            >
                <Icon className={styles.icon} icon={documentsArrow} size='16' />
                Документы платформы
            </Label>
            <TransitionComponent in={isOpen} timeout={animation}>
                <Text component='ul' textSize='18'>
                    {docs ? docs.map(doc => (
                        <div key={doc.Uid}>
                            <Link
                                download
                                href={getPublicLink(doc, clientofficeUrl)}
                                icon={download}
                                className={styles.link}
                            >
                                {`${doc.DocumentTypeName} `}
                                <Note>
                                    {`${doc.FileExtension.substr(1)}, `}
                                    {bytes(doc.FileSize, { decimalPlaces: 1 })}
                                </Note>
                            </Link>
                        </div>
                    )) : <LoadingIndicator />}
                </Text>
            </TransitionComponent>
        </div>
    );
});
